import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { recruitingAPI } from "../services/recruitingApi";
import "./CandidateAssessment.css";

interface CandidateAssessmentProps {
  candidateId: string | number;
  jobId: string | number;
  onClose: () => void;
}

interface TechnicalSkills {
  score?: number;
  match?: string;
  strengths?: string[];
  gaps?: string[];
  recommendations?: string;
}

interface SkillAnalysis {
  technicalSkills?: TechnicalSkills;
}

interface AssessmentSchema {
  overallFit?: {
    score?: number;
    summary?: string;
  };
  strengths?: string[];
  weaknesses?: string[];
  potentiallyMissing?: string[];
  recommendations?: {
    hiring?: string;
  };
  skillAnalysis?: SkillAnalysis;
  interviewQuestions?: string[];
}

interface AssessmentData {
  schema?: AssessmentSchema;
  overallFit?: {
    score?: number;
    summary?: string;
  };
  strengths?: string[];
  weaknesses?: string[];
  potentiallyMissing?: string[];
  recommendations?: {
    hiring?: string;
  };
  skillAnalysis?: SkillAnalysis;
  interviewQuestions?: string[];
}

interface FitScore {
  overall?: number;
  technical?: number;
}

interface Recommendations {
  hiring?: string;
  hiringDecision?: string;
  priority?: string;
  nextSteps?: string[];
  interviewQuestions?: string[];
}

interface Metadata {
  candidateName: string;
  jobTitle: string;
}

interface Assessment {
  fitScore?: FitScore;
  assessment?: AssessmentData;
  recommendations?: Recommendations;
  metadata: Metadata;
  generatedAt: string;
}

type ActiveTab = "overview" | "skills" | "interview";

const CandidateAssessment: React.FC<CandidateAssessmentProps> = ({
  candidateId,
  jobId,
  onClose,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>("overview");

  const generateAssessment = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = (await recruitingAPI.assessCandidateForRole(
        candidateId,
        jobId,
      )) as unknown;
      // Handle both wrapped and direct response structures
      const assessmentData =
        (response as { data?: Assessment }).data || (response as Assessment);
      setAssessment(assessmentData);
    } catch (err) {
      setError((err as Error).message || "Failed to generate assessment");
    } finally {
      setLoading(false);
    }
  }, [candidateId, jobId]);

  useEffect(() => {
    if (candidateId && jobId) {
      generateAssessment();
    }
  }, [candidateId, jobId, generateAssessment]);

  const getScoreColor = (score: number): string => {
    if (score >= 80) return "#28a745";
    if (score >= 65) return "#ffc107";
    if (score >= 50) return "#fd7e14";
    return "#dc3545";
  };

  const getScoreLabel = (score: number): string => {
    if (score >= 80) return t("assessment.scoreLabels.excellent");
    if (score >= 65) return t("assessment.scoreLabels.good");
    if (score >= 50) return t("assessment.scoreLabels.fair");
    return t("assessment.scoreLabels.poor");
  };

  const renderScoreCircle = (
    score: number,
    label: string,
    size: "medium" | "large" = "medium",
  ) => {
    const sizeClass =
      size === "large" ? "score-circle-large" : "score-circle-medium";
    const color = getScoreColor(score);

    return (
      <div
        className={`score-circle ${sizeClass}`}
        style={{ borderColor: color }}
      >
        <div className="score-value" style={{ color }}>
          {score}
        </div>
        <div className="score-label">{label}</div>
      </div>
    );
  };

  const renderOverviewTab = () => {
    if (!assessment) return null;

    const {
      fitScore,
      assessment: assessmentData,
      recommendations,
    } = assessment;

    // Handle nested schema structure from AI response
    const assessmentSchema = assessmentData?.schema || assessmentData;
    const overallFit = assessmentSchema?.overallFit || {};
    const strengths = assessmentSchema?.strengths || [];
    const weaknesses = assessmentSchema?.weaknesses || [];
    const potentiallyMissing = assessmentSchema?.potentiallyMissing || [];
    const recs = assessmentSchema?.recommendations || recommendations || {};

    return (
      <div className="assessment-overview">
        <div className="overview-header">
          <h3>{t("assessment.overview.overallAssessment")}</h3>
          <div className="overall-score">
            {renderScoreCircle(
              fitScore?.overall || overallFit?.score || 0,
              getScoreLabel(fitScore?.overall || overallFit?.score || 0),
              "large",
            )}
          </div>
        </div>

        <div className="score-breakdown">
          <div className="score-item">
            {renderScoreCircle(
              fitScore?.technical || 0,
              t("assessment.scoreLabels.technical"),
            )}
          </div>
        </div>

        <div className="assessment-summary">
          <h4>{t("assessment.overview.assessmentSummary")}</h4>
          <p>{overallFit?.summary || t("assessment.overview.noSummary")}</p>
        </div>

        <div className="recommendations-section">
          <h4>{t("assessment.overview.recommendations")}</h4>
          <div className="recommendation-card">
            <div className="recommendation-header">
              <span className="recommendation-label">
                {t("assessment.overview.hiringDecision")}
              </span>
              <span
                className={`recommendation-value ${recs.hiring ? "high" : "medium"}`}
              >
                {recs.hiring ||
                  recommendations?.hiringDecision ||
                  t("assessment.overview.review")}
              </span>
            </div>
            {recommendations?.priority && (
              <div className="recommendation-header">
                <span className="recommendation-label">
                  {t("assessment.overview.priority")}
                </span>
                <span
                  className={`recommendation-value ${recommendations.priority}`}
                >
                  {recommendations.priority.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {strengths.length > 0 && (
          <div className="strengths-section">
            <h4>{t("assessment.overview.strengths")}</h4>
            <ul>
              {strengths.map((strength, index) => (
                <li key={`strength-${index}-${strength}`}>{strength}</li>
              ))}
            </ul>
          </div>
        )}

        {weaknesses.length > 0 && (
          <div className="weaknesses-section">
            <h4>{t("assessment.overview.areasForImprovement")}</h4>
            <ul>
              {weaknesses.map((weakness, index) => (
                <li key={`weakness-${index}-${weakness}`}>{weakness}</li>
              ))}
            </ul>
          </div>
        )}

        {potentiallyMissing.length > 0 && (
          <div className="potentially-missing-section">
            <h4>{t("assessment.overview.potentiallyMissing")}</h4>
            <p className="missing-description">
              {t("assessment.overview.missingDescription")}
            </p>
            <ul>
              {potentiallyMissing.map((skill, index) => (
                <li key={`missing-${index}-${skill}`}>{skill}</li>
              ))}
            </ul>
          </div>
        )}

        {recommendations?.nextSteps && recommendations.nextSteps.length > 0 && (
          <div className="next-steps">
            <h4>{t("assessment.overview.nextSteps")}</h4>
            <ul>
              {recommendations.nextSteps.map((step, index) => (
                <li key={`step-${index}-${step}`}>{step}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderSkillsTab = () => {
    if (!assessment) return null;

    const { assessment: assessmentData } = assessment;
    // Handle nested schema structure
    const assessmentSchema = assessmentData?.schema || assessmentData;
    const skillAnalysis = assessmentSchema?.skillAnalysis || {};

    return (
      <div className="assessment-skills">
        <div className="skills-section">
          <h4>{t("assessment.skills.technicalSkills")}</h4>
          <div className="skill-analysis">
            {skillAnalysis.technicalSkills?.score !== undefined && (
              <div className="skill-score">
                {t("assessment.skills.score", {
                  score: skillAnalysis.technicalSkills.score,
                })}
              </div>
            )}
            {skillAnalysis.technicalSkills?.match && (
              <p className="skill-match">
                {skillAnalysis.technicalSkills.match}
              </p>
            )}

            {skillAnalysis.technicalSkills?.strengths &&
              skillAnalysis.technicalSkills.strengths.length > 0 && (
                <div className="skill-strengths">
                  <h5>{t("assessment.skills.strengths")}</h5>
                  <ul>
                    {skillAnalysis.technicalSkills.strengths.map(
                      (strength, index) => (
                        <li key={`tech-strength-${index}-${strength}`}>
                          {strength}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              )}

            {skillAnalysis.technicalSkills?.gaps &&
              skillAnalysis.technicalSkills.gaps.length > 0 && (
                <div className="skill-gaps">
                  <h5>{t("assessment.skills.skillGaps")}</h5>
                  <ul>
                    {skillAnalysis.technicalSkills.gaps.map((gap, index) => (
                      <li key={`gap-${index}-${gap}`}>{gap}</li>
                    ))}
                  </ul>
                </div>
              )}

            {skillAnalysis.technicalSkills?.recommendations && (
              <div className="skill-recommendations">
                <h5>{t("assessment.skills.recommendations")}</h5>
                <p>{skillAnalysis.technicalSkills.recommendations}</p>
              </div>
            )}
          </div>
        </div>

        {assessmentSchema?.potentiallyMissing &&
          assessmentSchema.potentiallyMissing.length > 0 && (
            <div className="skills-section">
              <h4>{t("assessment.skills.potentiallyMissing")}</h4>
              <div className="skill-analysis">
                <p className="missing-description">
                  {t("assessment.skills.missingDescription")}
                </p>
                <ul className="missing-skills-list">
                  {assessmentSchema.potentiallyMissing.map((skill, index) => (
                    <li key={`pot-missing-${index}-${skill}`}>{skill}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderInterviewTab = () => {
    if (!assessment) return null;

    const { assessment: assessmentData, recommendations } = assessment;
    // Handle nested schema structure
    const assessmentSchema = assessmentData?.schema || assessmentData;
    const interviewQuestions =
      assessmentSchema?.interviewQuestions ||
      recommendations?.interviewQuestions ||
      [];

    return (
      <div className="assessment-interview">
        <div className="interview-section">
          <h4>{t("assessment.interview.technicalQuestions")}</h4>
          <p className="interview-description">
            {t("assessment.interview.description")}
          </p>
          <div className="interview-questions">
            {interviewQuestions.length > 0 ? (
              interviewQuestions.map((question, index) => (
                <div
                  key={`question-${index}-${question.substring(0, 20)}`}
                  className="question-item"
                >
                  <span className="question-number">{index + 1}.</span>
                  <span className="question-text">{question}</span>
                </div>
              ))
            ) : (
              <p>{t("assessment.interview.noQuestions")}</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="candidate-assessment-modal">
        <div className="modal-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t("assessment.generatingAssessment")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="candidate-assessment-modal">
        <div className="modal-content">
          <div className="error-container">
            <h3>{t("assessment.assessmentError")}</h3>
            <p>{error}</p>
            <button onClick={generateAssessment} className="retry-button">
              {t("assessment.retryAssessment")}
            </button>
            <button onClick={onClose} className="close-button">
              {t("common:actions.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="candidate-assessment-modal">
        <div className="modal-content">
          <div className="no-assessment">
            <h3>{t("assessment.noAssessment")}</h3>
            <p>{t("assessment.noAssessmentMessage")}</p>
            <button onClick={onClose} className="close-button">
              {t("common:actions.close")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="candidate-assessment-modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{t("assessment.title")}</h2>
          <button onClick={onClose} className="close-button">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="assessment-header">
            <div className="candidate-info">
              <h3>{assessment.metadata.candidateName}</h3>
              <p>
                {t("assessment.assessmentFor")}{" "}
                <strong>{assessment.metadata.jobTitle}</strong>
              </p>
              <p className="assessment-date">
                {t("assessment.generated")}{" "}
                {new Date(assessment.generatedAt).toLocaleString()}
              </p>
            </div>
          </div>

          <div className="assessment-tabs">
            <button
              className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              {t("assessment.tabs.overview")}
            </button>
            <button
              className={`tab-button ${activeTab === "skills" ? "active" : ""}`}
              onClick={() => setActiveTab("skills")}
            >
              {t("assessment.tabs.skillsAnalysis")}
            </button>
            <button
              className={`tab-button ${activeTab === "interview" ? "active" : ""}`}
              onClick={() => setActiveTab("interview")}
            >
              {t("assessment.tabs.interviewGuide")}
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "overview" && renderOverviewTab()}
            {activeTab === "skills" && renderSkillsTab()}
            {activeTab === "interview" && renderInterviewTab()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateAssessment;
